import React, { PureComponent } from 'react'
import {
  DsDivider,
  DsImage,
  DsRadio,
  DsRadioGroup,
  DsStack,
  DsTypography
} from '@am92/react-design-system'

import withPageConstants, {
  IWithPageConstantsProps
} from '~/src/Hocs/withPageConstants'

import SUBSCRIPTION_CARD_PAGE_CONSTANTS from './SubscriptionCard.PageConstants'
import { T_SUBSCRIPTION_CARD_PAGE_CONSTANTS } from './SubscriptionCard.PageConstants/TYPES'

import {
  CURRENCY_MAP,
  SUBSCRIPTION_IMAGE_MAP
} from '~/src/Entities/Subscription/Subscription.Constants'
import {
  I_SUBSCRIPTION,
  I_SUBSCRIPTION_TENURES,
  T_CURRENCY,
  T_SUBSCRIPTION_PLAN_NAMES
} from '~/src/Entities/Subscription/Subscription.Types'

export interface ISubscriptionCardProps
  extends IWithPageConstantsProps<T_SUBSCRIPTION_CARD_PAGE_CONSTANTS> {
  subscription: I_SUBSCRIPTION
}

export interface ISubscriptionCardState {
  selectedTenure: I_SUBSCRIPTION_TENURES['periodInMonths']
}

class SubscriptionCard extends PureComponent<
  ISubscriptionCardProps,
  ISubscriptionCardState
> {
  constructor(props: ISubscriptionCardProps) {
    super(props)

    this.state = {
      selectedTenure: props?.subscription?.tenures[0].periodInMonths
    }
  }

  getSelectedTenureObj = (): I_SUBSCRIPTION_TENURES => {
    const { selectedTenure } = this.state
    const { subscription } = this.props
    const tenureObj: I_SUBSCRIPTION_TENURES =
      subscription.tenures.find(
        tenure => tenure.periodInMonths === selectedTenure
      ) || subscription.tenures[0]

    return tenureObj
  }

  handleTenureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event
    const { value } = target
    this.setState({ selectedTenure: value })
  }

  render() {
    const { PAGE_CONSTANTS, subscription } = this.props
    const { selectedTenure } = this.state
    const { tenures } = subscription
    const PLAN_CONSTANTS =
      PAGE_CONSTANTS[
        subscription.planName.toLocaleUpperCase() as T_SUBSCRIPTION_PLAN_NAMES
      ]

    const selectedTenureObj = this.getSelectedTenureObj()
    return (
      <DsStack
        sx={{
          pb: {
            xs: 'var(--ds-spacing-frostbite)',
            md: 'var(--ds-spacing-mild)'
          },
          px: {
            xs: 'var(--ds-spacing-frostbite)',
            md: 'var(--ds-spacing-mild)'
          },
          pt: 'var(--ds-spacing-blazing)',
          border: '0.5px solid var(--ds-colour-strokeDefault)',
          background: 'linear-gradient(180deg, #EDEDED 0%, #E9E9E9 99.36%)',
          borderRadius: 'var(--ds-radius-bitterCold)'
        }}
      >
        <DsStack
          spacing='var(--ds-spacing-bitterCold)'
          sx={{ pb: 'var(--ds-spacing-cool)' }}
        >
          <DsImage
            srcSet={
              SUBSCRIPTION_IMAGE_MAP[
                subscription.planName.toLocaleUpperCase() as T_SUBSCRIPTION_PLAN_NAMES
              ]
            }
            aspectRatio={1.7}
            ErrorIconProps={{ fontSize: 'scorched' }}
            style={{ height: '100%' }}
          />
          <DsStack spacing='var(--ds-spacing-quickFreeze)'>
            <DsTypography variant='headingBoldItalicLarge' color='primary'>
              {PLAN_CONSTANTS.TITLE}
            </DsTypography>
            <DsTypography variant='bodyRegularMedium'>
              {PLAN_CONSTANTS.DESCRIPTION.replace(
                '{{minUser}}',
                subscription.minChilds.toString()
              )}
            </DsTypography>
          </DsStack>

          <DsDivider />
        </DsStack>
        <DsStack
          spacing='var(--ds-spacing-frostbite)'
          sx={{ pb: 'var(--ds-spacing-cool)' }}
        >
          <DsTypography variant='subheadingSemiboldLarge'>
            {PLAN_CONSTANTS.TENURE_LABEL_TEXT}
          </DsTypography>
          <DsRadioGroup
            name='tenureId'
            value={selectedTenure}
            onChange={this.handleTenureChange}
            sx={{
              '> label:not(:first-of-type)': {
                mt: 'var(--ds-spacing-frostbite)'
              }
            }}
          >
            {tenures.map((tenure, index) => (
              <DsRadio
                key={index}
                value={tenure.periodInMonths}
                label={PLAN_CONSTANTS.TENURES_MAP[tenure.periodInMonths]}
                sx={{
                  marginLeft: 'var(--ds-spacing-zero)'
                }}
                RadioProps={{
                  sx: { padding: 'var(--ds-spacing-zero)' }
                }}
              />
            ))}
          </DsRadioGroup>
          <DsDivider />
        </DsStack>
        <DsStack spacing='var(--ds-spacing-frostbite)'>
          <DsTypography variant='subheadingSemiboldLarge'>
            {PLAN_CONSTANTS.YOU_WILL_PAY_TEXT}
          </DsTypography>
          <DsTypography variant='displayBoldLarge'>
            <DsTypography
              variant='headingBoldExtraSmall'
              sx={{ verticalAlign: 'top' }}
            >
              {CURRENCY_MAP[selectedTenureObj.amountCurrency as T_CURRENCY]}{' '}
            </DsTypography>
            {selectedTenureObj.amountPerUser}
            <DsTypography variant='headingBoldExtraSmall'>
              / {PLAN_CONSTANTS.YOU_WILL_PAY_USER_TEXT}
            </DsTypography>
          </DsTypography>
        </DsStack>
      </DsStack>
    )
  }
}

export default withPageConstants<T_SUBSCRIPTION_CARD_PAGE_CONSTANTS>(
  SUBSCRIPTION_CARD_PAGE_CONSTANTS
)(SubscriptionCard)
